import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { NotificationService } from '../services/notification.service';
import { ApiErrorResponse } from '../types/api-error-response';

export const HIDE_ERRORS = new HttpContextToken(() => false);

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private isRefreshing: boolean = false;
  private tokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private router: Router,
    private authService: AuthService,
    private notificationService: NotificationService,
    private translocoService: TranslocoService,
  ) {}

  t(text: string) {
    return this.translocoService.translate('http-error.' + text);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((err: any) => {
        if (err instanceof HttpErrorResponse) {
          if (this.isUnauthorizedOrInvalidGrantError(err) && !req.url.includes('3motionai')) {
            if (!req.url.includes('/token') && this.authService.hasRefreshToken()) {
              return this.refreshTokenAndRetry(req, next);
            } else {
              this.logout();
            }
          } else if (this.isForbiddenError(err)) {
            const authToken = this.authService.getAccessToken();
            if (authToken) {
              const payload = JSON.parse(atob(authToken.split('.')[1]));
              if (payload?.['enterprise_ids'] && payload['enterprise_ids'].length === 0) {
                return this.refreshTokenAndRetry(req, next);
              }
            }
          } else if (this.isMaintenanceError(err)) {
            this.router.navigateByUrl('/maintenance');
            return throwError(err);
          }

          const hideErrors = req.context.get(HIDE_ERRORS);
          if (!hideErrors) {
            this.informUser(err);
          }
        }

        return throwError(err);
      }),
    );
  }

  private isUnauthorizedOrInvalidGrantError(err: HttpErrorResponse): boolean {
    return (
      err.status === HttpStatusCode.Unauthorized ||
      (err.error?.error === 'invalid_grant' && err.error?.error_description?.startsWith('Invalid refresh token'))
    );
  }

  private isForbiddenError(err: HttpErrorResponse): boolean {
    return err.status === HttpStatusCode.Forbidden;
  }

  private informUser(apiErr: ApiErrorResponse): void {
    if (apiErr.status === 401) {
      if (apiErr.error.error_description?.toLowerCase().includes('expired')) {
        this.notificationService.displayInfo(
          this.t('Your account has been logged out due to inactivity. Please login again.'),
        );
      } else {
        this.notificationService.displayError(this.t('Unauthorized request. Please try again later.'));
      }
    } else if (['unauthorized_user', 'access_denied'].includes(apiErr.error?.error)) {
      this.notificationService.displayError(apiErr.error.error_description);
    } else if (apiErr.status === 409) {
      if (!apiErr.error.error.includes(this.t('User does not have an active employment.'))) {
        this.notificationService.displayError(apiErr.error.error);
      }
    } else if (apiErr.status >= 400 && apiErr.status < 500) {
      if (this.isUnauthorizedOrInvalidGrantError(apiErr)) {
        this.notificationService.displayInfo(this.t('Your session has expired. Please log in again to continue.'));
      } else {
        this.notificationService.displayError(apiErr.error.error);
      }
    } else {
      this.notificationService.displayError(
        this.t('An error has occurred, please try again. If this problem persists contact support.'),
      );
    }
  }

  private refreshTokenAndRetry(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.tokenSubject.next(null);
      return this.refreshTokens(req, next);
    }

    return this.tokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((_) => next.handle(req)),
    );
  }

  private refreshTokens(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    return this.authService.refresh().pipe(
      switchMap((success: boolean) => {
        this.isRefreshing = false;

        this.tokenSubject.next(success);

        return next.handle(req);
      }),
      catchError((err) => {
        this.isRefreshing = false;
        this.logout();

        return throwError(err);
      }),
    );
  }

  private logout() {
    this.authService.logout();
  }

  private isMaintenanceError(err: HttpErrorResponse): boolean {
    return err.status == 512;
  }
}
